import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ArrowLeft, Gavel, Paperclip } from 'lucide-react';
import { PageShell } from '@/components/common/PageShell';
import { Badge, Button, Card, Spinner } from '@/components/ui';
import {
  appealMyCase,
  downloadMyCaseAttachment,
  getMyCase,
  respondToMyCase,
  withdrawMyGrievance,
  type HrCaseEntry,
  type MyHrCaseDetail,
} from '@/api/services/hrCases.service';
import { getErrorMessage } from '@/lib/getErrorMessage';
import { FormShell, TextArea } from '../cases/caseFields';
import { caseTitle } from './myCaseFormat';
import {
  APPEAL_LABELS,
  CASE_STATUS,
  DECISION_LABELS,
  FINDING_LABELS,
  ENTRY_LABELS,
  formatBytes,
  formatCaseDate,
  formatCaseDateTime,
  LABEL_CLASS,
  saveBlob,
  TERMINAL_STATUSES,
} from '../cases/caseFormat';

/**
 * Submits one party action. The request id is minted once per open form, so
 * a retry after a network failure is not recorded twice.
 */
function usePartyAction<TInput>(
  caseId: string,
  run: (requestId: string, input: TInput) => Promise<unknown>,
  successMessage: string,
  onDone: () => void
) {
  const [requestId] = useState(() => crypto.randomUUID());
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (input: TInput) => run(requestId, input),
    onSuccess: () => {
      toast.success(successMessage);
      void queryClient.invalidateQueries({ queryKey: ['my-hr-cases'] });
      void queryClient.invalidateQueries({ queryKey: ['my-hr-case', caseId] });
      onDone();
    },
    onError: (mutationError) => setError(getErrorMessage(mutationError)),
  });
  return {
    submit: (input: TInput) => {
      setError(null);
      mutation.mutate(input);
    },
    isPending: mutation.isPending,
    error,
  };
}

function AppealForm({
  caseId,
  title,
  onDone,
}: {
  caseId: string;
  title: string;
  onDone: () => void;
}) {
  const [grounds, setGrounds] = useState('');
  const action = usePartyAction(
    caseId,
    (requestId, input: { grounds: string }) => appealMyCase(caseId, { requestId, ...input }),
    'Appeal submitted',
    onDone
  );
  return (
    <FormShell
      title={title}
      error={action.error}
      onCancel={onDone}
      onSubmit={() => action.submit({ grounds: grounds.trim() })}
      submitLabel="Submit appeal"
      disabled={grounds.trim() === ''}
      isPending={action.isPending}
    >
      <p className="text-sm text-gray-600 dark:text-gray-300">
        Someone who was not involved in the original decision hears your appeal.
      </p>
      <TextArea
        id="my-case-appeal-grounds"
        label="Grounds of appeal"
        value={grounds}
        onChange={setGrounds}
        required
        rows={5}
        hint="Explain why the decision should change. You can add documents as a response."
      />
    </FormShell>
  );
}

function ResponseForm({ caseId, onDone }: { caseId: string; onDone: () => void }) {
  const [body, setBody] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const action = usePartyAction(
    caseId,
    (requestId, input: { body: string; file?: File }) =>
      respondToMyCase(caseId, { requestId, ...input }),
    'Response sent',
    onDone
  );
  return (
    <FormShell
      title="Send a response"
      error={action.error}
      onCancel={onDone}
      onSubmit={() => action.submit({ body: body.trim(), ...(file && { file }) })}
      submitLabel="Send"
      disabled={body.trim() === ''}
      isPending={action.isPending}
    >
      <TextArea
        id="my-case-response"
        label="Your response"
        value={body}
        onChange={setBody}
        required
      />
      <div>
        <label htmlFor="my-case-response-file" className={LABEL_CLASS}>
          Attachment (optional)
        </label>
        <input
          id="my-case-response-file"
          type="file"
          accept=".pdf,.png,.jpg,.jpeg,.webp,.txt,.doc,.docx,.xlsx"
          onChange={(event) => setFile(event.target.files?.[0] ?? null)}
          className="block w-full text-sm text-gray-700 file:mr-3 file:rounded-lg file:border-0 file:bg-gray-100 file:px-3 file:py-2 file:text-sm file:font-medium dark:text-gray-200 dark:file:bg-gray-700"
          aria-describedby="my-case-response-file-hint"
        />
        <p
          id="my-case-response-file-hint"
          className="mt-1.5 text-xs text-gray-600 dark:text-gray-300"
        >
          PDF, image, text, Word, or Excel, up to 20 MB.
        </p>
      </div>
    </FormShell>
  );
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
        {label}
      </dt>
      <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">{children}</dd>
    </div>
  );
}

function SharedEntry({ caseId, entry }: { caseId: string; entry: HrCaseEntry }) {
  // Entries the employee wrote are addressed to themselves.
  const mine = Boolean(entry.author && entry.author.userId === entry.addressedTo?.userId);
  const download = async (index: number, name: string): Promise<void> => {
    try {
      saveBlob(await downloadMyCaseAttachment(caseId, entry._id, index), name);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };
  return (
    <li className="space-y-1.5 p-4">
      <div className="flex flex-wrap items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
        <span className="font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-200">
          {mine ? 'You' : (ENTRY_LABELS[entry.type] ?? entry.type)}
        </span>
        {!mine && <span>{entry.author?.name ?? 'HR'}</span>}
        <span aria-hidden>·</span>
        <time dateTime={entry.createdAt}>{formatCaseDateTime(entry.createdAt)}</time>
      </div>
      {entry.meeting && (
        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
          {formatCaseDateTime(entry.meeting.at)}
          {entry.meeting.location ? ` · ${entry.meeting.location}` : ''}
        </p>
      )}
      {entry.body && (
        <p className="whitespace-pre-line break-words text-sm text-gray-800 dark:text-gray-200">
          {entry.body}
        </p>
      )}
      {entry.attachments.map((attachment) => (
        <Button
          key={attachment.index}
          variant="secondary"
          size="sm"
          onClick={() => void download(attachment.index, attachment.originalName)}
        >
          <Paperclip className="mr-1.5 h-4 w-4" aria-hidden />
          {attachment.originalName}
          <span className="ml-1.5 text-xs text-gray-500 dark:text-gray-400">
            {formatBytes(attachment.size)}
          </span>
        </Button>
      ))}
    </li>
  );
}

function WithdrawForm({ caseId, onDone }: { caseId: string; onDone: () => void }) {
  const [reason, setReason] = useState('');
  const action = usePartyAction(
    caseId,
    (requestId, input: { reason?: string }) => withdrawMyGrievance(caseId, { requestId, ...input }),
    'Grievance withdrawn',
    onDone
  );
  return (
    <FormShell
      title="Withdraw your grievance"
      error={action.error}
      onCancel={onDone}
      onSubmit={() => action.submit({ reason: reason.trim() || undefined })}
      submitLabel="Withdraw"
      submitVariant="danger"
      disabled={false}
      isPending={action.isPending}
    >
      <p className="text-sm text-gray-600 dark:text-gray-300">
        The handlers stop working on it. You can raise a new grievance later.
      </p>
      <TextArea
        id="my-case-withdraw-reason"
        label="Reason (optional)"
        value={reason}
        onChange={setReason}
      />
    </FormShell>
  );
}

function CaseDetail({ hrCase }: { hrCase: MyHrCaseDetail }): React.ReactElement {
  const [form, setForm] = useState<'appeal' | 'respond' | 'withdraw' | null>(null);
  const discipline = hrCase.kind === 'discipline';
  const appealLabel = discipline ? 'Appeal this decision' : 'Appeal the findings';
  const status = CASE_STATUS[hrCase.status];
  const terminal = TERMINAL_STATUSES.includes(hrCase.status);
  const warning = hrCase.outcome?.warning;
  const done = (): void => setForm(null);
  return (
    <div className="space-y-4">
      <Card>
        <div className="space-y-5">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
              {caseTitle(hrCase)}
            </h2>
            <Badge variant={status?.variant ?? 'neutral'} dot>
              {status?.label ?? hrCase.status}
            </Badge>
            <span className="font-mono text-xs text-gray-500 dark:text-gray-400">
              {hrCase.reference}
            </span>
          </div>
          <dl className="grid gap-x-6 gap-y-4 sm:grid-cols-3">
            {discipline && <Fact label="Hearing">{formatCaseDateTime(hrCase.hearingAt)}</Fact>}
            {hrCase.outcome?.finding && (
              <Fact label="Finding">{FINDING_LABELS[hrCase.outcome.finding]}</Fact>
            )}
            {hrCase.outcome?.decision && (
              <Fact label="Decision">
                {DECISION_LABELS[hrCase.outcome.decision]}
                {warning && (
                  <span className="block text-xs text-gray-600 dark:text-gray-300">
                    {warning.label} · {warning.expired ? 'expired' : 'expires'}{' '}
                    {formatCaseDate(warning.expiresAt)}
                  </span>
                )}
              </Fact>
            )}
            {hrCase.appeal ? (
              <Fact label="Your appeal">
                {hrCase.appeal.decision
                  ? APPEAL_LABELS[hrCase.appeal.decision]
                  : 'Waiting for a decision'}
                <span className="block text-xs text-gray-600 dark:text-gray-300">
                  Sent {formatCaseDate(hrCase.appeal.requestedAt)}
                </span>
              </Fact>
            ) : (
              hrCase.appealClosesAt && (
                <Fact label="Appeal by">{formatCaseDate(hrCase.appealClosesAt)}</Fact>
              )
            )}
          </dl>
          {hrCase.party === 'respondent' && (
            <p className="text-sm text-gray-600 dark:text-gray-300">
              HR has asked for your input on a matter. You see only what is addressed to you, and
              you can reply below.
            </p>
          )}
          {warning && (
            <p className="text-sm text-gray-600 dark:text-gray-300">
              If HR issues a warning letter, you can read and sign it in{' '}
              <Link to="/my-letters" className="font-medium text-primary underline">
                My Letters
              </Link>
              .
            </p>
          )}
          {!terminal && (
            <div className="flex flex-wrap gap-2">
              {hrCase.canAppeal && (
                <Button size="sm" onClick={() => setForm('appeal')}>
                  {appealLabel}
                </Button>
              )}
              <Button size="sm" variant="secondary" onClick={() => setForm('respond')}>
                Send a response
              </Button>
              {hrCase.canWithdraw && (
                <Button size="sm" variant="ghost" onClick={() => setForm('withdraw')}>
                  Withdraw grievance
                </Button>
              )}
            </div>
          )}
          {form === 'appeal' && (
            <AppealForm caseId={hrCase._id} title={appealLabel} onDone={done} />
          )}
          {form === 'respond' && <ResponseForm caseId={hrCase._id} onDone={done} />}
          {form === 'withdraw' && <WithdrawForm caseId={hrCase._id} onDone={done} />}
        </div>
      </Card>
      <Card
        header={
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            Shared with you
          </h2>
        }
        contentClassName="p-0"
      >
        <ol className="divide-y divide-gray-200 dark:divide-gray-700">
          {hrCase.entries.map((entry) => (
            <SharedEntry key={entry._id} caseId={hrCase._id} entry={entry} />
          ))}
        </ol>
      </Card>
    </div>
  );
}

/**
 * One HR matter as its employee sees it: dates, their outcome, what HR has
 * shared with them, and their own responses and appeal. Viewing is logged.
 */
export function MyCasePage(): React.ReactElement {
  const { caseId } = useParams<{ caseId: string }>();
  const navigate = useNavigate();
  const { data, isLoading, error } = useQuery({
    queryKey: ['my-hr-case', caseId],
    queryFn: () => getMyCase(caseId!),
    enabled: Boolean(caseId),
    retry: false,
  });

  let body: React.ReactNode;
  if (isLoading) {
    body = (
      <Card>
        <div className="flex justify-center py-12" role="status">
          <Spinner />
          <span className="sr-only">Loading</span>
        </div>
      </Card>
    );
  } else if (error || !data) {
    body = (
      <Card>
        <p className="text-sm text-danger" role="alert">
          {error ? getErrorMessage(error) : 'This matter could not be found.'}
        </p>
      </Card>
    );
  } else {
    body = <CaseDetail hrCase={data} />;
  }

  return (
    <PageShell
      breadcrumbItems={[
        { label: 'Self Service', to: '/dashboard' },
        { label: 'My HR Cases', to: '/my-hr-cases' },
        { label: 'Case' },
      ]}
      icon={Gavel}
      title="HR case"
      className="gap-0"
      hideHeader
    >
      <div className="mx-auto w-full max-w-4xl space-y-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/my-hr-cases')}>
          <ArrowLeft className="mr-1.5 h-4 w-4" aria-hidden />
          My HR Cases
        </Button>
        {body}
      </div>
    </PageShell>
  );
}
